import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Tabs, Tab, Paper, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, TablePagination, IconButton, Button, Chip, Alert, CircularProgress,
  Tooltip, Stack, FormControlLabel, Switch, TextField, MenuItem, Select, InputLabel,
  FormControl,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import OutboxIcon from '@mui/icons-material/Outbox';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Checkbox,
} from '@mui/material';
import {
  listEmailQueue, cancelEmailQueueItem, sendQueuedEmailNow, sendAllReadyGlobal,
} from '../services/api';

const TABS = [
  { key: 'drafts',    label: 'Drafts',    status: 'Draft' },
  { key: 'scheduled', label: 'Scheduled', status: 'Scheduled' },
];

const STAGES = [
  'FirstContact', 'FollowUp', 'Qualification', 'ReEngagement',
  'DemoInvite', 'Negotiation', 'Closed',
];

function fmtDate(iso) {
  if (!iso) return 'â€”';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'â€”';
  return d.toLocaleString();
}

export default function EmailQueuePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') === 'scheduled') ? 'scheduled' : 'drafts';

  const [tab, setTab] = useState(initialTab);
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0); // MUI TablePagination is 0-based
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [mineOnly, setMineOnly] = useState(true);
  const [busyId, setBusyId] = useState(null);

  // Filters
  const [stageFilter, setStageFilter] = useState('');
  const [scheduledForFrom, setScheduledForFrom] = useState('');
  const [scheduledForTo, setScheduledForTo] = useState('');

  // Send-all dialog
  const [sendAllOpen, setSendAllOpen] = useState(false);
  const [sendAllIncludeNeedsReview, setSendAllIncludeNeedsReview] = useState(false);

  // Preview dialog
  const [previewItem, setPreviewItem] = useState(null);

  const currentStatus = TABS.find(t => t.key === tab)?.status || 'Draft';

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const fromIso = scheduledForFrom ? new Date(scheduledForFrom).toISOString() : null;
      const toIso = scheduledForTo ? new Date(scheduledForTo + 'T23:59:59').toISOString() : null;
      const { data } = await listEmailQueue(
        currentStatus, mineOnly, page + 1, pageSize,
        stageFilter || null, fromIso, toIso,
      );
      setItems(data.items || []);
      setTotalCount(data.totalCount || 0);
    } catch (err) {
      setError(err.response?.data || err.message || 'Failed to load queue');
    } finally {
      setLoading(false);
    }
  }, [currentStatus, mineOnly, page, pageSize, stageFilter, scheduledForFrom, scheduledForTo]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleTabChange = (_, newTab) => {
    setTab(newTab);
    setPage(0);
    const next = new URLSearchParams(searchParams);
    next.set('tab', newTab);
    setSearchParams(next, { replace: true });
  };

  const handleFilterChange = () => {
    setPage(0);
  };

  const handleEdit = (item) => {
    navigate(`/email-compose/${item.leadId}?queueId=${item.id}`);
  };

  const handleCancel = async (item) => {
    const label = currentStatus === 'Scheduled' ? 'cancel this scheduled email' : 'delete this draft';
    if (!window.confirm(`Are you sure you want to ${label}?`)) return;
    setBusyId(item.id);
    setError('');
    setInfo('');
    try {
      await cancelEmailQueueItem(item.id);
      setInfo(currentStatus === 'Scheduled' ? 'Scheduled email cancelled' : 'Draft deleted');
      await fetchItems();
    } catch (err) {
      setError(err.response?.data || err.message || 'Failed to cancel');
    } finally {
      setBusyId(null);
    }
  };

  const handleSendNow = async (item) => {
    if (!window.confirm(`Send this email now to ${item.recipientEmail || '(no recipient)'}?`)) return;
    setBusyId(item.id);
    setError('');
    setInfo('');
    try {
      const { data } = await sendQueuedEmailNow(item.id);
      if (data?.success === false) {
        setError(data.errorMessage || 'Send failed');
      } else {
        setInfo('Email sent');
      }
      await fetchItems();
    } catch (err) {
      const data = err.response?.data;
      setError(data?.errorMessage || data || err.message || 'Send failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleSendAllReady = async () => {
    setError('');
    setInfo('');
    try {
      const { data } = await sendAllReadyGlobal(sendAllIncludeNeedsReview, mineOnly);
      setSendAllOpen(false);
      setSendAllIncludeNeedsReview(false);
      setInfo(`Promoted ${data.promoted} email(s) to ReadyToSend (worker dispatches within ~30s). Skipped (NeedsReview): ${data.skippedNeedsReview}. Already ready: ${data.alreadyReady}.`);
      await fetchItems();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Send all failed');
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Email Queue</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControlLabel
            control={<Switch checked={mineOnly} onChange={(e) => { setMineOnly(e.target.checked); setPage(0); }} />}
            label="Mine only"
          />
          <Button startIcon={<OutboxIcon />} variant="contained" color="success" size="small" onClick={() => setSendAllOpen(true)}
            sx={{ textTransform: 'none', borderRadius: 1.5 }}>
            Send All Ready
          </Button>
          <Button startIcon={<RefreshIcon />} variant="outlined" size="small" onClick={fetchItems} disabled={loading}>
            Refresh
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{String(error)}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert>}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Stage</InputLabel>
            <Select
              value={stageFilter}
              label="Stage"
              onChange={(e) => { setStageFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All stages</MenuItem>
              {STAGES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
          {currentStatus === 'Scheduled' && (
            <>
              <TextField
                size="small"
                label="Scheduled from"
                type="date"
                value={scheduledForFrom}
                onChange={(e) => { setScheduledForFrom(e.target.value); setPage(0); }}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 160 }}
              />
              <TextField
                size="small"
                label="Scheduled to"
                type="date"
                value={scheduledForTo}
                onChange={(e) => { setScheduledForTo(e.target.value); setPage(0); }}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 160 }}
              />
            </>
          )}
          {(stageFilter || scheduledForFrom || scheduledForTo) && (
            <Button size="small" onClick={() => { setStageFilter(''); setScheduledForFrom(''); setScheduledForTo(''); setPage(0); }}>
              Clear filters
            </Button>
          )}
        </Stack>
      </Paper>

      <Paper>
        <Tabs value={tab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          {TABS.map(t => (
            <Tab key={t.key} value={t.key} label={t.label} />
          ))}
        </Tabs>

        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={32} /></Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Lead</TableCell>
                    <TableCell>Recipient</TableCell>
                    <TableCell>Subject</TableCell>
                    <TableCell>Stage</TableCell>
                    {currentStatus === 'Scheduled' && <TableCell>Scheduled For</TableCell>}
                    {currentStatus === 'Draft' && <TableCell>Updated</TableCell>}
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No {currentStatus === 'Scheduled' ? 'scheduled emails' : 'drafts'} found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map(item => (
                      <TableRow key={item.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.leadCompanyName || `Lead #${item.leadId}`}
                          </Typography>
                          {item.contactName && (
                            <Typography variant="caption" color="text.secondary">{item.contactName}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{item.recipientEmail || 'â€”'}</Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 320 }}>
                          <Typography variant="body2" noWrap title={item.subject}>{item.subject || '(no subject)'}</Typography>
                          {item.lastError && (
                            <Typography variant="caption" color="error" noWrap>{item.lastError}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {item.stage ? <Chip size="small" label={item.stage} /> : 'â€”'}
                        </TableCell>
                        {currentStatus === 'Scheduled' && (
                          <TableCell>
                            <Typography variant="body2">{fmtDate(item.scheduledFor)}</Typography>
                            {item.aiSendTimeReason && (
                              <Tooltip title={item.aiSendTimeReason}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: 'block', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                >
                                  AI: {item.aiSendTimeReason}
                                </Typography>
                              </Tooltip>
                            )}
                          </TableCell>
                        )}
                        {currentStatus === 'Draft' && (
                          <TableCell>
                            <Typography variant="body2">{fmtDate(item.createdAt)}</Typography>
                          </TableCell>
                        )}
                        <TableCell align="right">
                          <Tooltip title="Preview email">
                            <span>
                              <IconButton size="small" onClick={() => setPreviewItem(item)} disabled={busyId === item.id}>
                                <VisibilityOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <span>
                              <IconButton size="small" onClick={() => handleEdit(item)} disabled={busyId === item.id}>
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Send now">
                            <span>
                              <IconButton size="small" onClick={() => handleSendNow(item)} disabled={busyId === item.id || !item.recipientEmail}>
                                <SendIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={currentStatus === 'Scheduled' ? 'Cancel schedule' : 'Delete draft'}>
                            <span>
                              <IconButton size="small" color="error" onClick={() => handleCancel(item)} disabled={busyId === item.id}>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Paper>

      {/* Send-all dialog */}
      <Dialog open={sendAllOpen} onClose={() => setSendAllOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Send all ready</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Promotes every <strong>Draft</strong> {mineOnly ? 'created by you' : 'in the queue'} to <strong>ReadyToSend</strong>. The worker dispatches them on its next tick (~30s).
          </Typography>
          <FormControlLabel
            control={<Checkbox checked={sendAllIncludeNeedsReview} onChange={(e) => setSendAllIncludeNeedsReview(e.target.checked)} />}
            label={<Typography variant="body2">Also include NeedsReview (low quality)</Typography>}
          />
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setSendAllOpen(false)}>Cancel</Button>
          <Button size="small" variant="contained" color="success" onClick={handleSendAllReady}>Send all ready</Button>
        </DialogActions>
      </Dialog>

      {/* Email preview dialog */}
      <Dialog open={!!previewItem} onClose={() => setPreviewItem(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {previewItem?.subject || '(no subject)'}
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
            {previewItem?.recipientEmail && (
              <Typography variant="caption" color="text.secondary">To: {previewItem.recipientEmail}</Typography>
            )}
            {previewItem?.leadCompanyName && (
              <Typography variant="caption" color="text.secondary">{previewItem.leadCompanyName}</Typography>
            )}
            {previewItem?.stage && (
              <Chip size="small" label={previewItem.stage} />
            )}
            {previewItem?.scheduledFor && (
              <Typography variant="caption" color="text.secondary">Scheduled: {fmtDate(previewItem.scheduledFor)}</Typography>
            )}
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {previewItem?.body ? (
            /^<[a-z]/i.test(previewItem.body.trim()) ? (
              <iframe
                title="Email preview"
                srcDoc={previewItem.body}
                style={{ width: '100%', height: 480, border: 'none' }}
                sandbox="allow-same-origin"
              />
            ) : (
              <Box sx={{ p: 3, whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 14 }}>
                {previewItem.body}
              </Box>
            )
          ) : (
            <Box sx={{ p: 3, color: 'text.secondary' }}>(no body)</Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => { handleEdit(previewItem); setPreviewItem(null); }}>Edit</Button>
          <Button size="small" onClick={() => setPreviewItem(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
